import { Chip } from 'primereact/chip';
import { getColors } from '../../../Constants';
import Card from '../Card/Card';
import './MemberCardWrapper.css';
import ChevronLink from '../ChevronLink/ChevronLink';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import MemberCard, { MemberDataType } from '../MemberCard/MemberCard';
import { capitalizeFirstLetter } from '../../../utils/texts';
import { useCouncilPath } from '../../../hooks/useCouncilPath';
import useDimensions from '../../../hooks/useDimensions';

type MemberGroupData = {
    partyName: string;
    customCountText?: string;
    partyColor: string;
    count: number;
    members: MemberDataType[];
};

type Props = {
    tabIndex: number;
    chipText?: string;
    groupCountText?: string;
    className?: string;
    groupName: string;
    count: number;
    bgColor?: string;
    partyId?: number;
    memberGroupsData?: MemberGroupData[];
    expanded?: boolean;
    relatedChartDataset?: any[];
    extraDescriptionComponent?: React.ReactNode;
    photo?: string;
};

// TODO: Add more links and collapse functionality somewhere to the top of the component
const MemberCardWrapper = ({
    tabIndex,
    className,
    chipText,
    groupCountText,
    groupName,
    count,
    bgColor = getColors().primary,
    partyId,
    memberGroupsData = [],
    expanded = false,
    relatedChartDataset = [],
    extraDescriptionComponent
}: Props) => {
    const { t } = useTranslation(['members', 'translation']);
    const [isExpanded, setIsExpanded] = useState(expanded);
    const contentRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLHeadingElement>(null);
    const councilPath = useCouncilPath();
    const { width } = useDimensions();

    useEffect(() => {
        setIsExpanded(expanded);
    }, [expanded]);

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
        if (isExpanded && headerRef.current) {
            setTimeout(() => headerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 330);
        }
    };

    // Calculating the height of the content to animate the expansion
    useEffect(() => {
        if (isExpanded && contentRef.current) {
            contentRef.current.style.maxHeight = `${contentRef.current.scrollHeight}px`;
        } else if (contentRef.current) {
            contentRef.current.style.maxHeight = '0px';
        }
    }, [isExpanded]);

    const chipTemplate = (
        <>
            <span className='material-symbols-rounded'>group</span>
            <h3>
            {chipText
                ? `${count} ${chipText}`
                : t('MemberCardWrapper.countCouncilMembers', { count })}
            </h3>
        </>
    );

    return (
        <Card className='members-wrapper no-hover'>
            <div className='members-wrapper-header'>
                {isExpanded && (
                    <button type='button' className='toggle-expand-button' onClick={toggleExpand}>
                        <ChevronLink
                            chevronDirection='up'
                            text={width > 768 ? t('MemberCardWrapper.hideList') : undefined}
                            linkDisabled
                        />
                    </button>
                )}
                <h3 id='group-name-heading' ref={headerRef} style={{ color: bgColor }}>
                    {groupName}
                </h3>
                <Chip template={chipTemplate} style={{ backgroundColor: bgColor }} />
                {extraDescriptionComponent && (
                    extraDescriptionComponent
                )}
            </div>
            <div ref={contentRef} className='members-wrapper-toggleable-content'>
                {memberGroupsData.map((group: MemberGroupData, index: number) => {
                    const relatedChartDatasetIndex = relatedChartDataset.findIndex((dataset) => {
                        return dataset.label === group.partyName;
                    });
                    return (
                        <div
                            className='members-group'
                            id={`${tabIndex}-datasetIndex-${
                                relatedChartDatasetIndex < 0 ? 0 : relatedChartDatasetIndex
                            }`}
                            key={index}
                        >
                            <h4 style={{ color: group.partyColor }}>
                                {`${group.customCountText ?? group.partyName} - ${
                                    groupCountText !== undefined
                                        ? `${group.count} ${groupCountText}`
                                        : t('MemberCardWrapper.countCouncilMembers', { count: group.count })
                                }`}
                            </h4>
                            <div className='member-card-list'>
                                {group.members.map((member: MemberDataType, index: number) => {
                                    // TODO: Move inactive / active in other party to MemberCard component
                                    const newPartyData = 'newPartyData' in member ? member.newPartyData : undefined;
                                    const isNowFor = !!(member.inactive && newPartyData);
                                    const partyName = isNowFor ? newPartyData!.partyName : group.partyName;
                                    const partyColor = isNowFor ? newPartyData!.partyColor : group.partyColor;

                                    return (
                                        <MemberCard
                                            memberData={member}
                                            partyData={{
                                                partyName: partyName,
                                                partyColor: partyColor,
                                                isNowFor: isNowFor
                                            }}
                                            key={index}
                                            customCardContent={member?.customCardContent}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className='members-wrapper-bottom-buttons'>
                <button className='members-wrapper-show-list-button' onClick={toggleExpand}>
                    <ChevronLink
                        chevronDirection={isExpanded ? 'left-up' : 'left-down'}
                        text={isExpanded ? t('MemberCardWrapper.hideList') : t('MemberCardWrapper.showList')}
                        linkDisabled
                    />
                </button>
                {partyId && (
                    <button className='members-wrapper-link-button'>
                        <ChevronLink
                            chevronDirection='right'
                            text={t('MemberCardWrapper.partyDetail')}
                            linkUrl={councilPath('subjekty', partyId)}
                        />
                    </button>
                )}
            </div>
        </Card>
    );
};

export default MemberCardWrapper;
